/**
 * SignalingService - WebSocket signaling for WebRTC
 * Supports both direct mode (connect to Host) and relay mode (connect via relay server)
 */

import { eventBus } from './EventBus';
import { iceRacingService } from './IceRacingService';
import type { SignalingMessage } from '../types';

export type SignalingState = 'disconnected' | 'connecting' | 'connected' | 'error';
export type PeerStatus = 'online' | 'offline' | 'waiting';

// Peer info for status bar display
export interface PeerInfo {
  clientId: string;
  ip: string;
  fingerprint: string;
  joinTime: number;
}

// 生成 32 位 GUID（无横线）
function generateClientId(): string {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  }
  // Last resort: Math.random
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Relay mode configuration
export interface RelayConfig {
  serverUrl: string;  // e.g., ws://relay.example.com:880/ws
  peerId: string;     // Peer ID to connect to
}

// Timeout constants for relay mode
const RELAY_PEER_WAIT_TIMEOUT = 60 * 1000;  // 1 minute to wait for peer
const RELAY_MAX_SIGNALING_TIME = 2 * 60 * 1000;  // 2 minutes max for signaling after peer online

// 统一重连策略：网络层失败 / host_offline / ws 中途断开 都走这套
// 前 RECONNECT_FAST_ATTEMPTS 次以 RECONNECT_FAST_INTERVAL_MS 间隔快速重试；
// 之后从 RECONNECT_BACKOFF_BASE_MS 开始每次累加 1s，封顶 RECONNECT_MAX_DELAY_MS。
// 总次数无上限，由用户主动 disconnect 终止。
const RECONNECT_FAST_ATTEMPTS = 3;
const RECONNECT_FAST_INTERVAL_MS = 300;
const RECONNECT_BACKOFF_BASE_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;

// "连接稳定"判定阈值：ws OPEN 后持续不断开这么久才视为业务连接成功，才清零 attempt。
// 防御场景：server 拒绝/CDN 异常时 ws OPEN 几毫秒就 onclose(1006)，过早 reset 会让退避失效。
const STABLE_THRESHOLD_MS = 5000;

// GPU指纹采集
function getGPUFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as WebGLRenderingContext | null;
    if (!gl) return 'no-webgl';

    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : 'unknown';
    const vendor = debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : 'unknown';

    // 简单hash生成
    const str = `${vendor}|${renderer}`;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16).padStart(8, '0');
  } catch {
    return 'error';
  }
}

class SignalingService {
  private ws: WebSocket | null = null;
  private messageQueue: SignalingMessage[] = [];
  private reconnectTimer: number | null = null;
  private pingTimer: number | null = null;
  private _state: SignalingState = 'disconnected';
  private _sessionId: string | null = null;
  private _relayMode: boolean = false;
  private _relayConfig: RelayConfig | null = null;
  private _peerStatus: PeerStatus = 'offline';
  private _clientId: string = generateClientId(); // 客户端唯一标识（每次连接重新生成）
  private _fingerprint: string = getGPUFingerprint(); // GPU指纹

  // Relay mode timers
  private peerWaitTimer: number | null = null;  // 1 min wait for peer
  private signalingMaxTimer: number | null = null;  // 2 min max signaling time

  // 统一重连状态（替代旧的 host_offline 30 次/30s 上限策略）
  private reconnectAttempt: number = 0;             // 已尝试的重连次数（schedule 时 +1）
  private reconnectScheduleTimer: number | null = null;  // 等待下一次重连的 timer
  private reconnectActive: boolean = false;         // 用户当前是否希望保持连接
  private stableTimer: number | null = null;        // ws OPEN 后用以判定连接稳定的 timer

  // 信令服务器进程可达性（HTTP 探测，独立于 ws 状态）
  // null=未探测/未知；true=服务器在响应；false=网络/超时
  private _serverReachable: boolean | null = null;
  private reachProbeTimer: number | null = null;  // 5s 间隔探测的 timer

  get state(): SignalingState {
    return this._state;
  }

  get serverReachable(): boolean | null {
    return this._serverReachable;
  }

  get sessionId(): string | null {
    return this._sessionId;
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  get isRelayMode(): boolean {
    return this._relayMode;
  }

  get peerStatus(): PeerStatus {
    return this._peerStatus;
  }

  get clientId(): string {
    return this._clientId;
  }

  get fingerprint(): string {
    return this._fingerprint;
  }

  /** 日志前缀（自动携带 clientId） */
  private get _tag(): string {
    return `[Signaling][${this._clientId}]`;
  }

  // 带 clientId 前缀的日志方法
  private _log(...args: any[]) { console.log(this._tag, ...args); }
  private _warn(...args: any[]) { console.warn(this._tag, ...args); }
  private _error(...args: any[]) { console.error(this._tag, ...args); }

  /**
   * Connect to signaling server (direct mode)
   */
  connect(url?: string): Promise<void> {
    this.regenerateClientId(); // 每次连接生成新 clientId，避免服务器 Leave 竞态
    this._relayMode = false;
    this._relayConfig = null;
    return this.doConnect(url);
  }

  /**
   * Connect via relay server
   */
  connectRelay(config: RelayConfig): Promise<void> {
    this.regenerateClientId(); // 每次连接生成新 clientId，避免服务器 Leave 竞态
    this._relayMode = true;
    this._relayConfig = config;
    this.reconnectActive = true;
    this.resetReconnect();

    // Build relay URL with Client type, clientId and fingerprint
    const url = `${config.serverUrl}?Peer=${encodeURIComponent(config.peerId)}&type=Client&client=${this._clientId}&fingerprint=${this._fingerprint}`;
    this._log('Connecting to relay server:', url, 'fingerprint:', this._fingerprint);

    return this.doConnect(url).then(() => {
      // Start 1 minute timer waiting for peer
      this.startPeerWaitTimer();
    });
  }

  /**
   * Start timer waiting for peer (1 minute)
   */
  private startPeerWaitTimer(): void {
    this.stopPeerWaitTimer();
    this._log('Starting 1 minute peer wait timer');
    this.peerWaitTimer = window.setTimeout(() => {
      if (this._peerStatus !== 'online') {
        this._log('Peer wait timeout (1 minute), disconnecting');
        eventBus.emit('relay:peerTimeout', undefined as never);
        this.disconnect();
      }
    }, RELAY_PEER_WAIT_TIMEOUT);
  }

  /**
   * Stop peer wait timer
   */
  private stopPeerWaitTimer(): void {
    if (this.peerWaitTimer) {
      clearTimeout(this.peerWaitTimer);
      this.peerWaitTimer = null;
    }
  }

  /**
   * Start max signaling timer (no longer used - keep relay connected)
   */
  private startSignalingMaxTimer(): void {
    // Disabled: keep relay connected to maintain peer status
    // Host needs to know Client is still alive
    this._log('Relay connection will be kept alive');
  }

  /**
   * Stop max signaling timer
   */
  private stopSignalingMaxTimer(): void {
    if (this.signalingMaxTimer) {
      clearTimeout(this.signalingMaxTimer);
      this.signalingMaxTimer = null;
    }
  }

  /**
   * Disconnect relay WebSocket only (keep WebRTC alive)
   * Called when P2P is established or timeout
   */
  disconnectRelay(): void {
    if (!this._relayMode) return;

    this._log('Disconnecting relay WebSocket (P2P established or timeout)');
    this.cancelReconnect();
    this.stopPeerWaitTimer();
    this.stopSignalingMaxTimer();
    this.stopPing();

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close(1000, 'P2P established');
      }
      this.ws = null;
    }
    this._state = 'disconnected';
  }

  /**
   * Internal connect implementation
   */
  private doConnect(url?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      this.cleanup();
      this._state = 'connecting';

      // Build WebSocket URL
      let wsUrl: string;
      if (url) {
        wsUrl = url;
      } else {
        const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${proto}//${window.location.host}/ws`;
      }

      try {
        this.ws = new WebSocket(wsUrl);
      } catch (e) {
        this._state = 'error';
        reject(e);
        return;
      }

      const timeout = setTimeout(() => {
        // 触发条件放宽：'connecting'（连接卡住）和 'disconnected'（onclose 已触发但握手期未 schedule）都进入。
        // 唯一不该触发的是 'connected'（已经成功）。
        if (this._state !== 'connected') {
          this._state = 'error';
          this.cleanup();
          if (this._relayMode && this._relayConfig) {
            this.scheduleReconnect('connect-timeout');
          }
          reject(new Error('Connection timeout'));
        }
      }, 1000);  // 1s 节奏锚点：握手期失败统一靠这个 timeout 触发 schedule，避免几十毫秒内闪连

      this.ws.onopen = () => {
        clearTimeout(timeout);
        this._state = 'connected';
        // 不在此处立即 resetReconnect()！ws OPEN 不代表业务成功——
        // 比如 server 拒绝/CDN 异常时 OPEN 后毫秒级就 onclose(1006)，过早 reset 会让 attempt 永远卡在 1。
        // 改为：OPEN 持续 STABLE_THRESHOLD_MS 不断才视为稳定，那时再清零。
        this.scheduleStableReset();
        this._log('WebSocket OPEN' + (this._relayMode ? ' (relay mode)' : ''));
        eventBus.emit('signaling:open', undefined as never);
        // ws 连上后停止 HTTP 探测、重置可达性状态（由 ws 状态本身承载）
        this.stopServerReachProbe();
        this.flushQueue();

        // In direct mode, send join message
        // In relay mode, just wait for Host to send offer when it sees us online
        if (!this._relayMode) {
          this.send({ type: 'join', roomId: 'default' });
        }

        this.startPing();
        resolve();
      };

      this.ws.onclose = (event) => {
        const wasConnecting = this._state === 'connecting';
        this._state = 'disconnected';
        this.cancelStableReset();   // OPEN→稳定的判定取消（已断开就不算稳定）
        this.stopPing();
        // ws 一断就启动 HTTP 探测，区分"服务器挂"与"WS 单链路断"
        this.startServerReachProbe();
        // 仍要广播 signaling:close：让上层（WebRTCService / P2PAssistService）有机会
        // reset 内部状态（mainConnectionType / consumed 等），避免重连后状态残留导致
        // P2PAssist 不再启动。WebRTCService.handleSignalingClose 已有 waitingForHost
        // 与 fastReconnectMode 防护，重复广播不会触发额外重连。
        if (this._relayMode && this._relayConfig) {
          if (wasConnecting) {
            // 握手阶段就 close：不立即 schedule，由 1s timeout 兜底统一节奏，
            // 避免被 RST 时几十毫秒内 onclose 触发 300ms 闪连。
            this._log(`Relay onclose during handshake (code=${event.code}), waiting for 1s timeout`);
          } else {
            // 已连上后中途断开：立即 schedule，不浪费 1s
            clearTimeout(timeout);
            this._log(`Relay onclose after open (code=${event.code}), scheduling reconnect`);
            this.scheduleReconnect('ws-close');
          }
          eventBus.emit('signaling:close', { code: event.code, reason: event.reason });
          return;
        }
        clearTimeout(timeout);
        eventBus.emit('signaling:close', { code: event.code, reason: event.reason });
      };

      this.ws.onerror = () => {
        clearTimeout(timeout);
        this._state = 'error';
        eventBus.emit('signaling:error', new Error('WebSocket error'));
        if (this._state === 'connecting') {
          reject(new Error('Connection failed'));
        }
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event.data);
      };
    });
  }

  /**
   * Disconnect from signaling server
   */
  disconnect(): void {
    this.cancelReconnect();
    this.cleanup();
    this._state = 'disconnected';
    this._sessionId = null;
    this._peerStatus = 'offline';
  }

  /**
   * Regenerate clientId (call before reconnect if needed)
   */
  regenerateClientId(): void {
    this._clientId = generateClientId();
    this._log('Regenerated clientId');
  }

  /**
   * Get current relay config (for reconnection)
   */
  getRelayConfig(): RelayConfig | null {
    return this._relayConfig;
  }

  /**
   * Send a signaling message
   *
   * 诊断日志：P2P_ASSIST 消息曾出现"console 打了发送日志，host 0 收到"的现象，
   * 加上 SEND-P2P / SEND-FAIL / SEND-QUEUED 三类日志区分实际丢失环节：
   *   - 未见 SEND-P2P：调用方根本没进 send（上游条件早返）
   *   - 见 SEND-P2P 且 bufferedAmount 不断增长：WS 上行被堵（NAT/代理底层）
   *   - 见 SEND-P2P 但抓包无 outgoing：浏览器 ws.send 内部吞
   *   - 见 SEND-FAIL：ws.send 抛异常此前被静默
   *   - 见 SEND-QUEUED：调用时 ws 不是 OPEN，消息进队列
   */
  send(message: SignalingMessage): boolean {
    const msgType = (message as { type?: string })?.type;
    const isP2PAssist = msgType === 'p2p_assist';
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        const payload = JSON.stringify(message);
        if (isP2PAssist) {
        }
        this.ws.send(payload);
        eventBus.emit('signaling:message', { type: msgType ?? 'unknown', data: message, dir: 'out' });
        return true;
      } catch (e) {
        console.error('[Signaling][SEND-FAIL]', e, 'msgType=', msgType,
          'bufferedAmount=', this.ws.bufferedAmount,
          'readyState=', this.ws.readyState);
        this.messageQueue.push(message);
        return false;
      }
    } else {
      if (isP2PAssist) {
        console.warn('[Signaling][SEND-QUEUED]',
          'ws=', this.ws ? `state=${this.ws.readyState}` : 'null',
          'action=', (message as { action?: string }).action,
          'sessionId=', (message as { sessionId?: string }).sessionId);
      }
      this.messageQueue.push(message);
      return false;
    }
  }

  /**
   * Send WebRTC offer answer
   */
  sendAnswer(sdp: string, raceId?: string): void {
    const msg: SignalingMessage = {
      type: 'webrtcviewer',
      kind: 'answer',
      message: { type: 'answer', sdp },
    };
    if (raceId) {
      msg.raceId = raceId;
    }
    this.send(msg);
  }

  /**
   * Send ICE candidate
   */
  sendCandidate(candidate: RTCIceCandidateInit): void {
    this.send({
      type: 'webrtcviewer',
      kind: 'candidate',
      message: candidate as SignalingMessage['message'],
    });
  }

  /**
   * Handle incoming message
   */
  private handleMessage(data: string): void {
    try {
      let message = JSON.parse(data) as SignalingMessage;

      // Handle wrapped messages from signaling server (Host messages come as {from:..., data:{...}})
      const wrappedMsg = message as unknown as { from?: string; data?: SignalingMessage };
      if (wrappedMsg.from && wrappedMsg.data && typeof wrappedMsg.data === 'object') {
        // Unwrap the message - use the inner data as the actual message
        message = wrappedMsg.data;
        this._log('Unwrapped message from', wrappedMsg.from, ':', message.type);
      }

      // Store session ID
      if (message.sid) {
        this._sessionId = message.sid;
      }

      // Handle server logs
      if (message.type === 'server_log' && typeof message.message === 'string') {
      }

      // Handle agent restart notification
      if (message.type === 'agent_restart') {
        eventBus.emit('agent:restart', { message: typeof message.message === 'string' ? message.message : 'Agent restarting' });
      }

      // Handle file search status updates
      if (message.type === 'file_search_status') {
        const statusData = message as unknown as {
          status: string;
          progress?: number;
          current?: number;
          total?: number;
          phase?: string;
          message?: string;
        };
        eventBus.emit('file_search:status', statusData);
      }

      // Handle peer status (relay mode)
      if (message.type === 'peer_status') {
        const status = (message as { status?: string }).status as PeerStatus || 'offline';
        const peerType = (message as { peerType?: string }).peerType || 'Host';
        this._peerStatus = status;
        eventBus.emit('peer:status', { status, peerType });

        if (this._relayMode && peerType === 'Host') {
          if (status === 'online') {
            // Host is online, stop peer wait timer and start 2 min signaling timer
            this.stopPeerWaitTimer();
            this.cancelStableReset();  // 业务上 host 上线 → 显式 reset，无需等 5s 稳定
            this.resetReconnect();     // Host 上线，重置重连退避计数
            this.startSignalingMaxTimer();
          } else if (status === 'offline') {
            // Host is offline, emit disconnect event
            eventBus.emit('signaling:close', { code: 1000, reason: 'Host disconnected' });
          }
        }
      }

      // Handle crash info from Host (sent on new connection if Error.log exists)
      if (message.type === 'crash_info') {
        const content = (message as { content?: string }).content || '';
        if (content) {
          eventBus.emit('host:crashInfo', { content });
        }
      }

      // Handle peer list updates (for status bar display)
      if (message.type === 'peer_list') {
        const peerListMsg = message as { peers?: PeerInfo[]; total?: number };
        eventBus.emit('peers:update', {
          peers: peerListMsg.peers || [],
          total: peerListMsg.total || 0,
        });
      }

      // Handle ICE Racing status from Host
      if (message.type === 'race_status') {
        const statusMsg = message as {
          side?: string;
          elapsedMs?: number;
          sessions?: Array<{
            raceId: string;
            label: string;
            status: string;
            iceState?: string;
            connectionType?: string;
            turnServer?: string;
            actualTurnEntry?: string;
            connectTime?: number;
            isWinner?: boolean;
            remoteCandidateCount?: number;
            quality?: {
              packetsReceived: number;
              packetsLost: number;
              throughputKbps: number;
              packetsSent?: number;
              score?: number;
              rtt?: number;
            };
          }>;
          winner?: string;
          error?: string;
        };
        if (statusMsg.side === 'host') {
          eventBus.emit('race:host_status', {
            elapsedMs: statusMsg.elapsedMs || 0,
            sessions: statusMsg.sessions || [],
            winner: statusMsg.winner,
            error: statusMsg.error,
          });
        }
      }

      // Handle error messages
      if (message.type === 'error') {
        const errorMsg = message as { code?: string; message?: string };
        console.error(`${this._tag} Error: ${errorMsg.code} - ${errorMsg.message}`);

        // Handle specific error codes
        if (errorMsg.code === 'host_offline') {
          // relay 模式下走统一重连策略（无上限+退避）
          if (this._relayMode && this._relayConfig) {
            // 关掉当前 ws，让 onclose 触发 schedule（也可主动 schedule，二者幂等）
            if (this.ws) {
              this.ws.onclose = null; // 避免 onclose 再 schedule 一次（虽然幂等也避免噪音）
              this.ws.close();
              this.ws = null;
            }
            this.scheduleReconnect('host_offline');
            return; // Don't emit error, we're retrying
          }
          eventBus.emit('host:offline', undefined as never);
        } else if (errorMsg.code === 'max_clients_reached') {
          eventBus.emit('room:full', undefined as never);
        }

        eventBus.emit('signaling:error', new Error(errorMsg.message || 'Unknown error'));
      }

      // Emit for WebRTC handling
      if (message.type === 'webrtcviewer') {
        const { kind, message: payload } = message;
        const raceId = (message as { raceId?: string }).raceId;

        if (kind === 'offer') {
          if (payload?.sdp) {
            if (raceId && raceId.startsWith('p2p-assist-')) {
              // P2P Assist renegotiation offer (Host added video track, needs Answer)
              this._log('Received P2P Assist renegotiation OFFER for:', raceId);
              eventBus.emit('p2p_assist:renegotiate_offer', { raceId, sdp: payload.sdp });
            } else if (raceId) {
              // ICE Racing mode: offer with raceId
              this._log('Received OFFER for race:', raceId);
              eventBus.emit('race:offer', { raceId, offer: { type: 'offer', sdp: payload.sdp } });
            } else {
              // Normal mode
              this._log('Received OFFER from ' + (this._relayMode ? 'Host via relay' : 'server'));
              eventBus.emit('webrtc:offer', { type: 'offer', sdp: payload.sdp });
            }
          } else {
            this._warn('Offer received but no SDP');
          }
        } else if (kind === 'candidate' && payload) {
          if (raceId) {
            // ICE Racing mode: candidate with raceId
            eventBus.emit('race:candidate', { raceId, candidate: payload as RTCIceCandidateInit });
          } else {
            // Normal mode
            eventBus.emit('webrtc:candidate', payload as RTCIceCandidateInit);
          }
        }
      }

      // Handle P2P assist messages
      if (message.type === 'p2p_assist') {
        const assistMsg = message as unknown as {
          action: string;
          sessionId?: string;
          sdp?: string;
          candidate?: RTCIceCandidateInit;
          step?: number;
          stepName?: string;
          status?: string;
          error?: string;
          details?: string;
          chunkIndex?: number;
          totalChunks?: number;
          totalBytes?: number;
          // host_candidate_info 扩展字段
          candidateType?: string;
          url?: string;
          address?: string;
          port?: number;
        };

        // [P2PAssist] action 日志已禁用以减少控制台噪音
        // console.log('%c[P2PAssist] ' + assistMsg.action, 'color: #9C27B0; font-weight: bold', assistMsg);

        switch (assistMsg.action) {
          case 'answer':
            eventBus.emit('p2p_assist:answer', {
              sessionId: assistMsg.sessionId,
              sdp: assistMsg.sdp,
            });
            break;
          case 'candidate':
            eventBus.emit('p2p_assist:candidate', {
              sessionId: assistMsg.sessionId,
              candidate: assistMsg.candidate,
            });
            break;
          case 'step_update':
            eventBus.emit('p2p_assist:step_update', {
              sessionId: assistMsg.sessionId,
              step: assistMsg.step,
              stepName: assistMsg.stepName,
              status: assistMsg.status,
              error: assistMsg.error,
              details: assistMsg.details,
            });
            break;
          case 'quality_progress':
            eventBus.emit('p2p_assist:quality_progress', {
              sessionId: assistMsg.sessionId,
              chunkIndex: assistMsg.chunkIndex,
              totalChunks: assistMsg.totalChunks,
              totalBytes: assistMsg.totalBytes,
            });
            break;
          case 'switch_ready':
            eventBus.emit('p2p_assist:switch_ready', {
              sessionId: assistMsg.sessionId,
            });
            break;
          case 'host_candidate_info':
            eventBus.emit('p2p_assist:host_candidate_info', {
              sessionId: assistMsg.sessionId,
              candidateType: assistMsg.candidateType,
              url: assistMsg.url,
              address: assistMsg.address,
              port: assistMsg.port,
            });
            break;
        }
      }

      // Handle path migrate messages（docs/path_observer_design.md §2/§4.4）
      // host → 前端只有 switch_ready / error 两种回执；SDP/ICE 走现有 webrtcviewer+raceId 通道
      if (message.type === 'path_migrate') {
        const pmMsg = message as unknown as { action?: string; sessionId?: string; reason?: string };
        switch (pmMsg.action) {
          case 'switch_ready':
            this._log('PathMigrate switch_ready:', pmMsg.sessionId);
            eventBus.emit('path_migrate:switch_ready', { sessionId: pmMsg.sessionId || '' });
            break;
          case 'error':
            this._warn('PathMigrate error:', pmMsg.sessionId, pmMsg.reason);
            eventBus.emit('path_migrate:error', { sessionId: pmMsg.sessionId || '', reason: pmMsg.reason || 'unknown' });
            break;
        }
      }

      // Handle capture access denied/restored events (MSTSC minimized)
      if (message.type === 'capture_access_denied') {
        const captureMsg = message as { message?: string };
        this._log('Capture access denied:', captureMsg.message);
        eventBus.emit('capture:accessDenied', { message: captureMsg.message || 'Remote desktop may be minimized' });
      }

      if (message.type === 'capture_access_restored') {
        const captureMsg = message as { message?: string };
        this._log('Capture access restored:', captureMsg.message);
        eventBus.emit('capture:accessRestored', { message: captureMsg.message || 'Capture restored' });
      }

      // 内网 TURN 状态推送（响应 internal_turn_query / internal_turn_probe）
      if (message.type === 'internal_turn_status') {
        const m = message as {
          enabled?: boolean;
          discoveryComplete?: boolean;
          errorReason?: string;
          endpoints?: Array<{
            ip: string; port: number; protocols: string[];
            reachable: boolean; rttMs: number; lastProbeAt: number;
          }>;
        };
        eventBus.emit('internal_turn:status', {
          enabled: !!m.enabled,
          discoveryComplete: !!m.discoveryComplete,
          errorReason: m.errorReason || '',
          endpoints: m.endpoints || [],
        });
      }

      // Generic message event
      eventBus.emit('signaling:message', { type: message.type, data: message, dir: 'in' });
    } catch (e) {
      this._error('Failed to parse message:', e);
    }
  }

  /**
   * Flush queued messages
   */
  private flushQueue(): void {
    while (this.messageQueue.length > 0 && this.isConnected) {
      const msg = this.messageQueue.shift();
      if (msg) {
        this.ws!.send(JSON.stringify(msg));
      }
    }
  }

  /**
   * Start ping interval to keep connection alive
   */
  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      if (this.isConnected) {
        this.send({ type: 'ping' });
      }
    }, 30000);
  }

  /**
   * Stop ping interval
   */
  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  /**
   * 启动信令服务器 HTTP 探测（ws 断开时调用）。
   * 5s 间隔 HEAD /api/host?peer=__probe__：
   *   任意 HTTP 响应(200/4xx/5xx) → reachable=true（进程在响应）
   *   网络错误/超时              → reachable=false
   * ws 重新连上后调 stopServerReachProbe() 停止并把 reachable 重置回 null。
   * 用于 UI 区分"信令服务器挂了" vs "仅 WS 断开（host 离线/握手被拒等）"。
   */
  private startServerReachProbe(): void {
    // 立即探一次，再每 5s 一次
    const probeOnce = async (): Promise<void> => {
      let reachable = false;
      try {
        const ctrl = new AbortController();
        const to = setTimeout(() => ctrl.abort(), 3000);
        try {
          // 注意：fetch 默认 GET。HEAD 在 nginx 上可能受路由限制，统一用 GET 但拒绝响应体。
          await fetch('/api/host?peer=__probe__', { method: 'GET', signal: ctrl.signal, cache: 'no-store' });
          reachable = true;  // 拿到任意 HTTP 响应即视为服务器在响应
        } finally {
          clearTimeout(to);
        }
      } catch {
        reachable = false;
      }
      if (this._serverReachable !== reachable) {
        this._serverReachable = reachable;
        eventBus.emit('signaling:server_reachable', { reachable });
      }
    };
    if (this.reachProbeTimer !== null) return;  // 已在探测中
    void probeOnce();
    this.reachProbeTimer = window.setInterval(() => { void probeOnce(); }, 5000);
  }

  private stopServerReachProbe(): void {
    if (this.reachProbeTimer !== null) {
      clearInterval(this.reachProbeTimer);
      this.reachProbeTimer = null;
    }
    if (this._serverReachable !== null) {
      this._serverReachable = null;
      // 不 emit：ws 已 open，UI 应该以 ws 状态为准；让订阅方在 ws 状态变化时自然覆盖
    }
  }

  /**
   * Send crash delete request to Host
   * Called when user clicks "I know" button on crash notification
   */
  sendCrashDelete(): void {
    if (this.isConnected) {
      this.send({ type: 'crash_delete' });
      this._log('Sent crash_delete request');
    }
  }

  /**
   * Send crash test request to Host (for debugging)
   * WARNING: This will crash the Host process!
   */
  sendCrashTest(): void {
    if (this.isConnected) {
      this.send({ type: 'crash_test' });
      this._log('Sent crash_test request - Host will crash!');
    }
  }

  /**
   * Send restart capture request to Host
   * Used when MSTSC was minimized and user wants to restore capture
   */
  sendRestartCapture(): void {
    if (this.isConnected) {
      this.send({ type: 'restart_capture' });
      this._log('Sent restart_capture request');
    }
  }

  /**
   * Send switch to login screen request to Host
   * This will force GDI capture mode which can capture Windows login screen
   */
  sendSwitchToLoginScreen(): void {
    if (this.isConnected) {
      this.send({ type: 'switch_to_login_screen' });
      this._log('Sent switch_to_login_screen request');
    }
  }

  /**
   * 计算下一次重连应等待的毫秒数（基于 reconnectAttempt 当前值）。
   * 调用前先 ++reconnectAttempt。
   *   attempt 1..3      → 300ms
   *   attempt 4..       → min((attempt-3)*1000, 30000) ms
   */
  private nextReconnectDelay(): number {
    if (this.reconnectAttempt <= RECONNECT_FAST_ATTEMPTS) {
      return RECONNECT_FAST_INTERVAL_MS;
    }
    const backoff = (this.reconnectAttempt - RECONNECT_FAST_ATTEMPTS) * RECONNECT_BACKOFF_BASE_MS;
    return Math.min(backoff, RECONNECT_MAX_DELAY_MS);
  }

  /**
   * 安排一次重连。幂等：已存在 pending timer 时直接返回。
   * 仅在 relay 模式 + reconnectActive=true 时生效。
   */
  private scheduleReconnect(reason: string): void {
    if (!this._relayMode || !this._relayConfig) return;
    if (!this.reconnectActive) return;
    if (this.reconnectScheduleTimer !== null) return;  // 已有 pending，幂等

    this.reconnectAttempt++;
    const delay = this.nextReconnectDelay();
    this._log(`scheduleReconnect attempt=${this.reconnectAttempt} delay=${delay}ms reason=${reason}`);

    eventBus.emit('host:waiting', {
      attempt: this.reconnectAttempt,
      maxAttempts: 0,  // 0 表示无上限（A 选项语义）
      reason,           // 'host_offline' / 'ws-close' / 'connect-timeout' / 'ws-error' 等，UI 据此分流文案
    });

    this.reconnectScheduleTimer = window.setTimeout(() => {
      this.reconnectScheduleTimer = null;
      if (!this.reconnectActive || !this._relayConfig) return;
      this._log(`Retrying relay connection (attempt ${this.reconnectAttempt})...`);
      const url = `${this._relayConfig.serverUrl}?Peer=${encodeURIComponent(this._relayConfig.peerId)}&type=Client&client=${this._clientId}&fingerprint=${this._fingerprint}`;
      this.doConnect(url).catch((e) => {
        this._error('Retry connect failed:', e);
        // doConnect 内部已挂 onclose/onerror/timeout，会再次 scheduleReconnect
      });
    }, delay);
  }

  /**
   * 重置重连状态：清零计数 + 取消 pending timer。
   * 用于成功连上、host 上线、用户主动重新 connect 时。
   */
  private resetReconnect(): void {
    if (this.reconnectScheduleTimer !== null) {
      clearTimeout(this.reconnectScheduleTimer);
      this.reconnectScheduleTimer = null;
    }
    this.reconnectAttempt = 0;
  }

  /**
   * 取消重连：用户主动 disconnect，不再尝试重连。
   */
  private cancelReconnect(): void {
    this.reconnectActive = false;
    this.resetReconnect();
    this.cancelStableReset();
  }

  /**
   * 启动 STABLE_THRESHOLD_MS 稳定判定 timer：到时仍未 onclose 就视为稳定，清零 attempt。
   * 进入时若已有 timer 先 cancel，幂等。
   */
  private scheduleStableReset(): void {
    this.cancelStableReset();
    this.stableTimer = window.setTimeout(() => {
      this._log(`Connection stable, resetReconnect (was attempt=${this.reconnectAttempt})`);
      this.stableTimer = null;
      this.resetReconnect();
    }, STABLE_THRESHOLD_MS);
  }

  /**
   * 取消稳定判定 timer。在 onclose / cancelReconnect / peer_status:online 内调用。
   */
  private cancelStableReset(): void {
    if (this.stableTimer !== null) {
      clearTimeout(this.stableTimer);
      this.stableTimer = null;
    }
  }

  /**
   * 是否处于重连等待状态（外部 WebRTCService 用以判断是否要触发额外重连）。
   * 语义保留：当前正在尝试 / 等待重连。
   */
  get waitingForHost(): boolean {
    return this.reconnectActive && (this.reconnectAttempt > 0 || this.reconnectScheduleTimer !== null);
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    this.stopPing();
    this.stopPeerWaitTimer();
    this.stopSignalingMaxTimer();
    // 注意：cleanup() 不取消 reconnectScheduleTimer，也不重置 reconnectAttempt。
    // doConnect 入口会调 cleanup()，但调用者可能正是统一重连流程本身（reconnectScheduleTimer fired）；
    // 取消/重置由 cancelReconnect()（用户主动 disconnect）/ resetReconnect()（连上或 host 上线）显式处理。

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;

      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }
  }
}

// Singleton instance
export const signalingService = new SignalingService();
export default signalingService;
